import { ConfirmationModal, ControlledForm, ErrorPage, Icon, IconButton, InfoCardInner, LoadingPage, Modal, TConfirmationModalProps, TInfoCardSectionProps, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { CountryName } from '../../components/coutryName/CountryName';
import { getReadableWorkAnniversaryVariation, getWorkAnniversaryIcon } from '../../entities/companies/shared';
import { getReadableRelationshipType } from '../../entities/dependants/shared';
import { getUpdateGiftExtraAttributesSchema } from '../../entities/gifts/schema';
import { getReadableGiftType, getRemovedInfoFromRecipientData, isDonationGift, printGiftLabels } from '../../entities/gifts/shared';
import { isDependant } from '../../entities/recipients/shared';
import { TDependantType, TGiftStatuses, TGiftTypes, TWorkAnniversaryGiftVariations, useDeleteGiftMutation, useMoveGiftMutation, useRemoveGiftFromGroupMutation, useUpdateGiftMutation } from '../../generated/graphql';
import { GIFT_PATH } from '../../routes/paths';
import { MovingToDoneModalContent } from './MovingToDoneModalContent';
import { newHireGiftsAssemblingColumnsDetails } from './NewHireGifts';
import { subscriptionGiftsAssemblingColumnsDetails } from './SubscriptionGifts';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { AlertTooltipDismiss } from '../../components/alerts/AlertTooltipDismiss';
import SplitButton from '../../components/splitButton/SplitButton';
import { useFilter } from '../../contexts/filters';
import { getReadableAddressProperty, getReadableDeliveryMethodType, isAddressValid } from '../../entities/addresses/shared';
import { TEmployeeType } from '../../entities/employees/queries';
import { TGiftType, useQueryAllGroupedAssemblingGifts, useQueryOneGift } from '../../entities/gifts/queries';
import { useAuthContextUserEmail } from '../../entities/shared/auth';
import { BRICK_COLOR_CATALOG } from '../../entities/events/oneOff';
import styles from './GiftDetails.module.scss';

type TGiftExtraAttributesForm = Pick<TGiftType, 'rack' | 'notes'>;

export const GiftDetailsInner = (gift: TGiftType) => {
  const { filtersModalMode } = useFilter();
  useEffect(() => {
    filtersModalMode();
  }, []); // eslint-disable-line

  const [moveLoading, setMoveLoading] = useState<boolean>();
  const [deleteLoading, setDeleteLoading] = useState<boolean>();
  const [confirmationProps, setConfirmationProps] = useState<Omit<TConfirmationModalProps, 'closeModal' | 'confirmLoading' | 'onCancelClick'>>();
  const [moveTo, setMoveTo] = useState<TGiftStatuses>();
  const [giftsGoingToDone, setGiftsGoingToDone] = useState<boolean>(false);

  const navigate = useNavigate();
  const { state } = useLocation();
  const giftsList = useMemo(() => (state.giftsList ?? []) as TGiftType[], [state]);
  //console.log('state', giftsList);
  const userEmail = useAuthContextUserEmail();
  const [updateGift] = useUpdateGiftMutation();

  const columnsDetails = useMemo(() => {
    if (gift.type !== TGiftTypes.NewHire) return subscriptionGiftsAssemblingColumnsDetails;
    else return newHireGiftsAssemblingColumnsDetails;
  }, [gift.type]);

  const giftColumn = useMemo(() => {
    return columnsDetails.find((c) => c.id === gift.status);
  }, [columnsDetails, gift.status]);

  const possibleMoveToColumns = useMemo(() => {
    if (giftColumn?.destinationColumns) return columnsDetails.filter((c) => giftColumn.destinationColumns?.includes(c.id));
    else return [];
  }, [columnsDetails, giftColumn]);

  const nextGift = useMemo(() => {
    if (!giftsList || !giftsList.length) return undefined;
    const list = giftsList.filter((g) => g.status === gift.status);
    const index = list.findIndex((g) => g.id === gift.id);
    if (index > -1)
      //console.log('giftsList', giftsList);
      //console.log('list', list);
      //console.log('this', list[index]);
      //console.log('next', list[index + 1]);
      return giftsList[index + 1];
    else return undefined;
  }, [gift.id, gift.status, giftsList]);

  const [moveGift] = useMoveGiftMutation();

  const closeModal = useCallback(() => {
    navigate(generatePath('../'));
  }, [navigate]);

  const actuallyMoveGift = useCallback(() => {
    if (moveTo)
      if (moveTo === TGiftStatuses.Done)
        if (gift.address == null) toast.error('You cannot move a gift with no address to done.');
        else setGiftsGoingToDone(true);
      else {
        setMoveLoading(true);
        moveGift({
          variables: {
            id: gift.id,
            companyId: gift.companyId,
            version: gift._version,
            input: {
              status: moveTo
            }
          }
        })
          .then(() => {
            if (nextGift) {
              toast.success(`Gift successfully moved to ${moveTo}. Showing next gift on ${nextGift.status} column.`);

              navigate(generatePath(`../${GIFT_PATH}`, { giftId: nextGift.id }), { state: { giftsList } });
            } else closeModal();
          })
          .catch((error) => {
            toast.error(serializeError(error)?.message);
          })
          .then(() => {
            setMoveLoading(false);
          });
      }
  }, [closeModal, gift.companyId, gift._version, gift.address, gift.id, moveGift, moveTo, navigate, nextGift, giftsList]);

  const [deleteGift] = useDeleteGiftMutation();

  const onActualDelete = useCallback(() => {
    setDeleteLoading(true);
    deleteGift({
      variables: {
        id: gift.id,
        companyId: gift.companyId,
        version: gift._version
      }
    })
      .then(() => {
        closeModal();
      })
      .catch((error) => {
        console.error(serializeError(error));
        toast.error('There was an error deleting the gift.');
      })
      .then(() => {
        setDeleteLoading(false);
        setConfirmationProps(undefined);
      });
  }, [closeModal, deleteGift, gift._version, gift.companyId, gift.id]);

  const onDeleteClick = useCallback(() => {
    setConfirmationProps({
      headline: 'Delete gift?',
      supportingText: `Are you sure you want to delete this gift to ${gift.recipient?.fullName} from company ${gift.company?.name}?`,
      confirmLabel: 'Delete',
      onConfirmClick: onActualDelete
    });
  }, [gift, onActualDelete]);

  const [removeGiftFromGroup, { loading: removeGiftFromGroupLoading }] = useRemoveGiftFromGroupMutation();

  const onActualRemoveFromGroup = useCallback(() => {
    removeGiftFromGroup({
      variables: {
        id: gift.id,
        companyId: gift.companyId,
        version: gift._version
      }
    })
      .then(() => {
        closeModal();
      })
      .catch((error) => {
        console.error(serializeError(error));
        toast.error('There was an error removing the gift from the group');
      })
      .then(() => {
        setConfirmationProps(undefined);
      });
  }, [closeModal, gift._version, gift.companyId, gift.id, removeGiftFromGroup]);

  const onRemoveFromGroupClick = useCallback(() => {
    setConfirmationProps({
      headline: 'Remove gift from group?',
      supportingText: `Are you sure you want to remove this gift to ${gift.recipient?.fullName} from its group?`,
      confirmLabel: 'Remove from group',
      onConfirmClick: onActualRemoveFromGroup
    });
  }, [onActualRemoveFromGroup, gift]);

  const isDonation = useMemo(() => {
    return isDonationGift({
      gift
    });
  }, [gift]);

  const missingAddress = useMemo(() => {
    return gift.addressId == null || !isAddressValid(gift.address);
  }, [gift.addressId, gift.address]);

  const [printLoading, setPrintLoading] = useState<boolean>();

  const onPrintClick = useCallback(async () => {
    setPrintLoading(true);

    try {
      if (gift)
        await printGiftLabels({
          gifts: [gift]
        });
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error trying to print labels.');
    } finally {
      setPrintLoading(false);
    }
  }, [gift]);

  const topComponent = useMemo(() => {
    return (
      <div className={styles.topComponent}>
        <div className={styles.topTitleContainer}>
          <h2 className={styles.topTitle}>Gift</h2>
          <div className={styles.topIcons}>
            {gift.recreatedFromId && (
              <Tooltip message='This gift was recreated from a shipment.'>
                <Icon icon='dynamic_feed' />
              </Tooltip>
            )}
            {gift.type === TGiftTypes.WorkAnniversary && gift.company?.workAnniversaryGiftVariation && gift.company.workAnniversaryGiftVariation !== TWorkAnniversaryGiftVariations.Deluxe && (
              <Tooltip message={`Work Anniversary Variation: ${getReadableWorkAnniversaryVariation(gift.company.workAnniversaryGiftVariation)}`}>{getWorkAnniversaryIcon(gift.company.workAnniversaryGiftVariation)}</Tooltip>
            )}
            {isDonation && (
              <Tooltip message='Donation Enabled'>
                <Icon icon='volunteer_activism' />
              </Tooltip>
            )}
            {missingAddress && (
              <Tooltip message='Missing address data'>
                <Icon icon='not_listed_location' />
              </Tooltip>
            )}
            <AlertTooltipDismiss entity={gift} userEmail={userEmail} updateMutation={updateGift} />
          </div>
        </div>
        <div className={styles.topActions}>
          <Tooltip message='Print Gift label'>
            <IconButton icon='print' onClick={onPrintClick} disabled={printLoading || removeGiftFromGroupLoading || deleteLoading} variant='outlined' />
          </Tooltip>
          {gift.group != null && (
            <Tooltip message='Remove gift from group'>
              <IconButton icon='group_off' onClick={onRemoveFromGroupClick} disabled={printLoading || removeGiftFromGroupLoading || deleteLoading} variant='outlined' />
            </Tooltip>
          )}
          <Tooltip message='Delete gift'>
            <IconButton icon='delete' onClick={onDeleteClick} disabled={printLoading || removeGiftFromGroupLoading || deleteLoading} variant='outlined' />
          </Tooltip>
        </div>
      </div>
    );
  }, [gift, isDonation, missingAddress, onPrintClick, printLoading, removeGiftFromGroupLoading, deleteLoading, onRemoveFromGroupClick, onDeleteClick, userEmail, updateGift]);

  const sections = useMemo(() => {
    const secs: TInfoCardSectionProps<TGiftType>[] = [];

    secs.push({
      title: 'Gift',
      topComponent: () => topComponent,
      columns: _.compact([
        {
          label: 'Type',
          render: () => (gift.type === TGiftTypes.Other ? gift.customType : getReadableGiftType(gift)) as string
        },
        {
          label: 'Event Date',
          render: () => readableDate(gift.eventDate)
        },
        gift.recipient != null && isDependant(gift.recipient) && gift.recipient.type === TDependantType.Child
          ? {
              label: 'Age',
              render: () => gift.event?.age?.toString()
            }
          : undefined,
        gift.recipient != null && !isDependant(gift.recipient) && gift.type === TGiftTypes.WorkAnniversary
          ? {
              label: 'Age',
              render: () => gift.event?.age?.toString()
            }
          : undefined,
        gift.type === TGiftTypes.WorkAnniversary && {
          label: 'Work Anniversary Variation',
          render: () => getReadableWorkAnniversaryVariation(gift.company?.workAnniversaryGiftVariation || undefined)
        },
        gift.type === TGiftTypes.WorkAnniversary &&
          gift.company?.workAnniversaryGiftVariation === TWorkAnniversaryGiftVariations.Custom && {
            label: 'Custom Work Anniversary Notes',
            render: () => gift.company?.customWorkAnniversaryNotes
          },
        {
          label: 'Donation',
          render: () => (isDonation ? 'yes' : 'no')
        }
      ])
    });

    secs.push({
      title: 'Recipient Information',
      columns: _.compact([
        {
          label: 'Recipient Name',
          render: () => gift.recipient?.fullName
        },
        gift.recipient && isDependant(gift.recipient)
          ? {
              label: 'Relationship',
              render: () => `${getReadableRelationshipType(gift.recipient)} of ${gift.employee?.fullName}`
            }
          : undefined,
        {
          label: 'Food Allergies',
          render: () => gift.recipient?.foodPreferences
        },
        {
          label: 'T-shirt size',
          render: () => (gift.recipient as TEmployeeType).tShirtSize || '-'
        },
        {
          label: 'Sweater size',
          render: () => (gift.recipient as TEmployeeType).sweaterSize || '-'
        }
      ])
    });

    if (gift.brickQuantity || gift.brickSize || gift.brickColor || gift.brickText)
      secs.push({
        title: 'Brick Information',
        columns: [
          { label: 'Brick Quantity', render: () => String(gift.brickQuantity ?? 1) },
          { label: 'Brick Size', render: () => gift.brickSize || '-' },
          {
            label: 'Brick Color',
            render: () => {
              if (!gift.brickColor) return '-';
              const color = BRICK_COLOR_CATALOG[gift.brickColor];
              return (
                <div className={styles.colorIndicatorContainer}>
                  {color?.label || gift.brickColor}
                  <div className={styles.colorIndicator} style={{ backgroundColor: color?.background || gift.brickColor }} />
                </div>
              );
            }
          },
          { label: 'Printed Text', render: () => <span style={{ whiteSpace: 'pre-wrap' }}>{gift.brickText || '-'}</span> }
        ]
      });

    secs.push({
      title: 'Customizations',
      columns: _.compact([
        gift.type === TGiftTypes.NewHire && gift.company?.legoColor
          ? {
              label: 'Lego Color',
              render: () => {
                return (
                  <div className={styles.colorIndicatorContainer}>
                    {gift.company?.legoColor}
                    <div className={styles.colorIndicator} style={{ backgroundColor: gift.company?.legoColor || undefined }} />
                  </div>
                );
              }
            }
          : undefined,
        gift.type === TGiftTypes.NewHire && gift.company?.hasCustomOnboardBrick
          ? {
              label: 'Custom Onboard Brick',
              render: () => {
                if (gift.recipient && !isDependant(gift.recipient) && gift.recipient.customOnboardBrick != null) return gift.recipient.customOnboardBrick;
              }
            }
          : undefined,
        {
          label: 'Tissue Color',
          render: () => {
            if (gift.company?.tissueColor)
              return (
                <div className={styles.colorIndicatorContainer}>
                  {gift.company.tissueColor}
                  <div className={styles.colorIndicator} style={{ backgroundColor: gift.company.tissueColor }} />
                </div>
              );
            else return null;
          }
        },
        {
          label: 'Sign As',
          render: () => gift.company?.signAs
        },
        {
          label: 'Special Note',
          render: () => gift.company?.specialNote
        }
      ])
    });

    const deliveryAddress = getReadableDeliveryMethodType(gift) as string;
    secs.push({
      title: deliveryAddress ? `${deliveryAddress} Address` : 'No Delivery Method',
      columns: [
        {
          label: 'Country',
          render: () => {
            const country = getReadableAddressProperty(gift, 'country');
            if (country) return <CountryName name={country} />;
          }
        },
        {
          label: 'State',
          render: () => getReadableAddressProperty(gift, 'state')
        },
        {
          label: 'City',
          render: () => getReadableAddressProperty(gift, 'city')
        },
        {
          label: 'Address 1',
          render: () => getReadableAddressProperty(gift, 'address1')
        },
        {
          label: 'Address 2',
          render: () => getReadableAddressProperty(gift, 'address2')
        },
        {
          label: 'Postal Code',
          render: () => getReadableAddressProperty(gift, 'zipCode')
        }
      ]
    });

    return secs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gift, topComponent]);

  const onGiftUpdate = useCallback(
    (data: TGiftExtraAttributesForm) => {
      return updateGift({
        variables: {
          id: gift.id,
          companyId: gift.companyId,
          version: gift._version,
          input: {
            rack: data.rack == null ? null : data.rack,
            notes: data.notes == null ? null : data.notes
          }
        }
      }).catch((error) => {
        console.error(serializeError(error));
        toast.error('There was an error updating the gift.');
      });
    },
    [gift._version, gift.companyId, gift.id, updateGift]
  );

  const onMoveToDoneEnd = useCallback(() => {
    setGiftsGoingToDone(false);
    closeModal();
  }, [closeModal]);

  const { data: allGifts } = useQueryAllGroupedAssemblingGifts();

  const movingToDoneGifts = useMemo(() => {
    return giftsGoingToDone ? allGifts.filter((g) => g.id === gift.id) : [];
  }, [allGifts, gift.id, giftsGoingToDone]);

  return (
    <>
      {giftsGoingToDone && (
        <MovingToDoneModalContent
          //allGifts={allGifts}
          gifts={movingToDoneGifts}
          onCancel={onMoveToDoneEnd}
          onSuccess={onMoveToDoneEnd}
        />
      )}
      {!_.isEmpty(confirmationProps) && (
        <ConfirmationModal {...confirmationProps} closeModal={() => setConfirmationProps(undefined)} confirmLoading={deleteLoading || removeGiftFromGroupLoading} onCancelClick={() => setConfirmationProps(undefined)} />
      )}
      <Modal
        closeModal={closeModal}
        size='large'
        headline={gift.recipient?.fullName ? `Gift for ${gift.recipient.fullName}` : getRemovedInfoFromRecipientData(gift)}
        supportingText={`From ${gift.company?.name ?? '-'}`}
        contentClassName={styles.modal}
        showCancelButton={false}
        showCustomButton={possibleMoveToColumns.length > 0}
        customButton={() => {
          return (
            <SplitButton
              loading={moveLoading}
              options={possibleMoveToColumns.map((c) => ({
                label: c.name,
                value: c.id
              }))}
              dropDownAction={setMoveTo}
              buttonAction={actuallyMoveGift}
              disabled={moveLoading}
            />
          );
        }}>
        <div className={styles.giftDetailsContent}>
          <InfoCardInner<TGiftType> data={gift} sections={sections} />
          <div>
            {gift.type === TGiftTypes.WorkAnniversary && gift.company?.specialNote && (
              <div className={styles.giftSpecialNotes}>
                <span className={styles.giftSpecialNotesTitle}>Gift Special Notes</span>
                <span className={styles.giftSpecialNotesContent}>{gift.company?.specialNote}</span>
              </div>
            )}
            {gift.type === TGiftTypes.NewHire && gift.company?.swag && (
              <div className={styles.giftSpecialNotes}>
                <span className={styles.giftSpecialNotesTitle}>New Hire Notes</span>
                <span className={styles.giftSpecialNotesContent}>{gift.company?.swag}</span>
              </div>
            )}
            <ControlledForm<TGiftExtraAttributesForm>
              className={styles.form}
              fields={[
                {
                  type: 'textField',
                  label: 'Rack',
                  name: 'rack'
                },
                {
                  type: 'textArea',
                  label: 'Notes',
                  name: 'notes'
                }
              ]}
              formProps={{
                defaultValues: _.pick(gift, 'rack', 'notes'),
                resolver: joiResolver(getUpdateGiftExtraAttributesSchema(), {
                  convert: true,
                  abortEarly: false,
                  stripUnknown: false
                })
              }}
              onValidSubmit={onGiftUpdate}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export const GiftDetails = () => {
  const { giftId = '0' } = useParams();

  const { data: gift, error, loading } = useQueryOneGift({ id: giftId });
  //console.log('gift?:', gift);

  if (loading) return <LoadingPage />;

  if (error) {
    console.error('query Apollo ERROR:', error);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    if (!gift) return <ErrorPage error={error} />;
  }

  return <GiftDetailsInner {...gift} />;
};
